import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Payment } from "./models/Payment";
import { PaymentBucket } from "./models/PaymentBucket";
import { PaymentStatusChange } from "./models/PaymentStatusChange";
import { PaymentVisitLink } from "./models/PaymentVisitLink";
import {
  confirmPayment,
  createPackPurchasePayment,
  createStandalonePayment,
  downloadPaymentDocument,
  fetchPaymentBuckets,
  fetchPayments,
  fetchPaymentStatusHistory,
  fetchPaymentVisitLinks,
  linkVisitToPayment,
  markPaymentOverdue,
  markPaymentReceived,
  PaginatedPayments,
  unlinkVisitFromPayment,
  updatePaymentDocumentNote,
  voidPayment,
} from "./slice-extensions/payments-slice-extension";

interface PaymentsState {
  items: Payment[];
  page: number;
  pageSize: number;
  totalCount: number;
  status: LoadingStatus;
  saveStatus: LoadingStatus;
  statusHistoryById: Record<string, PaymentStatusChange[]>;
  visitLinksById: Record<string, PaymentVisitLink[]>;
  bucketsById: Record<string, PaymentBucket[]>;
  lastError?: ApiErrorResponse;
}

const initialState: PaymentsState = {
  items: [],
  page: 1,
  pageSize: 20,
  totalCount: 0,
  status: LoadingStatus.Idle,
  saveStatus: LoadingStatus.Idle,
  statusHistoryById: {},
  visitLinksById: {},
  bucketsById: {},
  lastError: undefined,
};

const upsert = (items: Payment[], updated: Payment): Payment[] =>
  items.some((i) => i.id === updated.id)
    ? items.map((i) => (i.id === updated.id ? updated : i))
    : [updated, ...items];

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearPaymentsError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchPayments.fulfilled, (state, action: PayloadAction<PaginatedPayments>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(createPackPurchasePayment.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(createPackPurchasePayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createPackPurchasePayment.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(createStandalonePayment.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(createStandalonePayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createStandalonePayment.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(confirmPayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(confirmPayment.rejected, (state, action) => {
        state.lastError = action.payload;
      })

      .addCase(markPaymentReceived.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(markPaymentOverdue.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(voidPayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })

      .addCase(updatePaymentDocumentNote.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(updatePaymentDocumentNote.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(downloadPaymentDocument.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(downloadPaymentDocument.rejected, (state, action) => {
        state.lastError = action.payload;
      })

      .addCase(markPaymentReceived.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(markPaymentOverdue.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(voidPayment.rejected, (state, action) => {
        state.lastError = action.payload;
      })

      .addCase(fetchPaymentStatusHistory.fulfilled,
        (state, action: PayloadAction<{ paymentId: string; changes: PaymentStatusChange[] }>) => {
          state.statusHistoryById[action.payload.paymentId] = action.payload.changes;
        })

      .addCase(fetchPaymentVisitLinks.fulfilled,
        (state, action: PayloadAction<{ paymentId: string; links: PaymentVisitLink[] }>) => {
          state.visitLinksById[action.payload.paymentId] = action.payload.links;
        })
      .addCase(linkVisitToPayment.fulfilled,
        (state, action: PayloadAction<{ paymentId: string; link: PaymentVisitLink }>) => {
          const current = state.visitLinksById[action.payload.paymentId] ?? [];
          const existing = current.find((l) => l.id === action.payload.link.id);
          state.visitLinksById[action.payload.paymentId] = existing
            ? current
            : [...current, action.payload.link];
        })
      .addCase(linkVisitToPayment.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(unlinkVisitFromPayment.fulfilled,
        (state, action: PayloadAction<{ paymentId: string; linkId: string }>) => {
          const current = state.visitLinksById[action.payload.paymentId] ?? [];
          state.visitLinksById[action.payload.paymentId] = current.filter(
            (l) => l.id !== action.payload.linkId);
        })
      .addCase(unlinkVisitFromPayment.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(fetchPaymentBuckets.fulfilled,
        (state, action: PayloadAction<{ paymentId: string; buckets: PaymentBucket[] }>) => {
          state.bucketsById[action.payload.paymentId] = action.payload.buckets;
        });
  },
});

export const { clearPaymentsError } = paymentsSlice.actions;

export const selectPayments = (state: RootState) => state.payments.items;
export const selectPaymentsByMember = (memberId: string) => (state: RootState) =>
  state.payments.items.filter((p) => p.memberId === memberId);
export const selectPaymentStatusHistory = (paymentId: string) => (state: RootState) =>
  state.payments.statusHistoryById[paymentId] ?? [];
export const selectPaymentVisitLinks = (paymentId: string) => (state: RootState) =>
  state.payments.visitLinksById[paymentId] ?? [];
export const selectPaymentBuckets = (paymentId: string) => (state: RootState) =>
  state.payments.bucketsById[paymentId] ?? [];
export const selectPaymentsStatus = (state: RootState) => state.payments.status;
export const selectPaymentsError = (state: RootState) => state.payments.lastError;

export default paymentsSlice.reducer;
